import { Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

const MIN_MS = 800;
const FADE_MS = 450;
const LOAD_CAP_MS = 2800;

function PageLoader({ leaving }) {
  const logo = `${process.env.PUBLIC_URL || ""}/logo-nav.webp`;

  return createPortal(
    <div
      className={`app-loader${leaving ? " is-leaving" : ""}`}
      role="status"
      aria-live="polite"
      aria-label="Loading"
    >
      <span className="app-loader__sr">Loading</span>
      <div className="app-loader__cluster">
        <div className="app-loader__glow" />
        <div className="app-loader__rings" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div className="app-loader__mark">
          <img src={logo} alt="Nafsikas garden logo" width="158" height="60" fetchPriority="high" />
        </div>
        <div className="app-loader__dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </div>
    </div>,
    document.body
  );
}

function SignalReady({ navId, startedAt, onLeave, onHide }) {
  useEffect(() => {
    if (!navId) return undefined;

    let hideTimer = 0;
    let dropTimer = 0;
    let capTimer = 0;
    let frame = 0;
    let secondFrame = 0;

    const begin = () => {
      const wait = Math.max(0, MIN_MS - (Date.now() - startedAt));
      hideTimer = window.setTimeout(() => {
        onLeave(navId);
        dropTimer = window.setTimeout(() => onHide(navId), FADE_MS);
      }, wait);
    };

    if (document.readyState === "complete") {
      frame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(begin);
      });
      return () => {
        window.cancelAnimationFrame(frame);
        window.cancelAnimationFrame(secondFrame);
        window.clearTimeout(hideTimer);
        window.clearTimeout(dropTimer);
      };
    }

    const onLoad = () => {
      window.clearTimeout(capTimer);
      begin();
    };
    capTimer = window.setTimeout(onLoad, LOAD_CAP_MS);
    window.addEventListener("load", onLoad, { once: true });

    return () => {
      window.removeEventListener("load", onLoad);
      window.clearTimeout(capTimer);
      window.clearTimeout(hideTimer);
      window.clearTimeout(dropTimer);
    };
  }, [navId, startedAt, onLeave, onHide]);

  return null;
}

export default function RouteLoading({ children }) {
  const { pathname } = useLocation();
  const navRef = useRef(0);
  const [nav, setNav] = useState({ id: 0, at: 0 });
  const [visible, setVisible] = useState(true);
  const [leaving, setLeaving] = useState(false);

  const onLeave = useCallback((id) => {
    if (id !== navRef.current) return;
    setLeaving(true);
  }, []);

  const onHide = useCallback((id) => {
    if (id !== navRef.current) return;
    setVisible(false);
  }, []);

  useLayoutEffect(() => {
    document.getElementById("boot-loader")?.remove();
    const id = navRef.current + 1;
    navRef.current = id;
    const at = id === 1 && window.__nfgLoaderStart ? window.__nfgLoaderStart : Date.now();
    setNav({ id, at });
    setVisible(true);
    setLeaving(false);
  }, [pathname]);

  useEffect(() => {
    if (!visible) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [visible]);

  return (
    <>
      {visible ? <PageLoader leaving={leaving} /> : null}
      <Suspense fallback={null}>
        {children}
        <SignalReady
          navId={nav.id}
          startedAt={nav.at}
          onLeave={onLeave}
          onHide={onHide}
        />
      </Suspense>
    </>
  );
}
